import {
  useAcceptedPlayReview,
  playReviewKey,
  NO_ACCEPTED_REVIEWS,
  type AcceptedPlayReviewRequest,
} from '@/containers/agents/use-accepted-play-review.hook';
import type { MessageQueue } from '@/components/agent-chat/message-queue-context';
import {
  parseQuestionCard,
  QUESTION_CARD_DISPLAY,
} from '@/components/agent-chat/question-card.util';
import {
  INTERACTION_DISPLAYS,
  THOUGHT_PART,
} from '@/components/agent-chat/steps.util';
import {
  markContinuedOn,
  shownText,
} from '@/components/agent-chat/paused-turn.util';
import {
  createScreenshotAttachmentAdapter,
  firstChatImage,
} from '@/components/agent-chat/screenshot-attachment';
import { useGumloopChat } from '@/components/agent-chat/use-gumloop-chat.hook';
import {
  enqueue,
  holdForCard,
  holdQueue,
  joinMessages,
  moveQueue,
  NEW_CHAT,
  NO_QUEUES,
  queueKey,
  queueKeysOfChat,
  releaseCardHold,
  removeQueued as removeQueuedMessage,
  returnCardHold,
  startedChatKey,
  takeAll,
  takeCardHold,
  takeQueued,
  type MessageQueues,
  type QueuedMessage,
} from '@/hooks/message-queue.util';
import {
  SCREENSHOT_MESSAGE,
  useAgentChat,
  type AgentContext,
  type AgentMessage,
  type ChatSnapshot,
  type ConfirmationRequest,
  type RunningTool,
  type UseAgentChatOptions,
} from '@/hooks/use-agent-chat.hook';
import { useAppSelector } from '@/store';
import {
  selectAiChatInitialMessage,
  selectAiChatPendingSend,
} from '@/store/features/ui.feature';
import type { LeadList } from '@/types';
import type {
  Unstable_DirectiveFormatter,
  Unstable_MentionAdapter,
  Unstable_MentionItem,
} from '@assistant-ui/core';
import {
  useExternalMessageConverter,
  useExternalStoreRuntime,
  type ExternalStoreAdapter,
  type ThreadMessageLike,
} from '@assistant-ui/react';
import { useUser } from '@clerk/react';
import type { ReadonlyJSONObject } from 'assistant-stream/utils';
import { usePostHog } from 'posthog-js/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type UseAgentChatRuntimeOptions = UseAgentChatOptions & {
  acceptedPlayReviews?: readonly AcceptedPlayReviewRequest[];
  enabled?: boolean;
  lists?: LeadList[];
  onSelectList?: (list: LeadList | null) => void;
  /** The session manager owns "New chat" and
   *  opening a history item. Omitted, this runtime clears / loads in place. */
  onNewThread?: () => void;
  onOpenConversation?: (conversationId: string) => void;
};

// Single dummy category id — the adapter exposes one category so the
// mention popover's keyboard navigation has a non-empty list even when
// the user has typed just '@'. The <Thread /> popover auto-selects
// this category on open so arrow keys navigate items directly.
export const LIST_MENTION_CATEGORY_ID = 'lists';

const NO_QUEUED: readonly QueuedMessage[] = Object.freeze([]);

// One adapter for every chat: it holds no state of its own.
const SCREENSHOT_ATTACHMENTS = createScreenshotAttachmentAdapter();

const SUGGESTED_PROMPTS = [
  { prompt: 'Find email addresses for this list' },
  { prompt: 'Qualify my leads against my ICP' },
  { prompt: 'Clean up the first names in this list' },
  { prompt: 'How many credits do I have?' },
];

const extractMessageText = (
  message: Parameters<
    NonNullable<ExternalStoreAdapter<AgentMessage>['onNew']>
  >[0]
) => {
  return message.content
    .filter(
      (part): part is { type: 'text'; text: string } => part.type === 'text'
    )
    .map((part) => part.text)
    .join('');
};

const toToolResultPart = (
  toolResult: NonNullable<AgentMessage['toolResults']>[number]
) => ({
  type: 'tool-call' as const,
  toolCallId: toolResult.id,
  toolName: 'tool_result',
  args: {
    id: toolResult.id,
    name: toolResult.name,
    displayType: toolResult.display?.type ?? 'generic',
    ...(toolResult.input_args
      ? { inputArgs: toolResult.input_args as unknown as ReadonlyJSONObject }
      : {}),
    // What the call did and how long it took, for the Gumloop style's step
    // row (steps.util toolStepView).
    ...(toolResult.caption ? { caption: toolResult.caption } : {}),
    ...(typeof toolResult.durationMs === 'number'
      ? { durationMs: toolResult.durationMs }
      : {}),
  },
  result: {
    ...toolResult.result,
    __toolResultId: toolResult.id,
    __toolName: toolResult.name,
    __displayType: toolResult.display?.type ?? 'generic',
    // The whole display payload: a card's own fields (a question card's
    // questions, quick replies' options, an agent update's fields) ride on
    // the display, not the result.
    __display: toolResult.display ?? {},
  },
});

/** A tool still working: a step with no result, labelled as running. */
const toRunningPart = (tool: RunningTool) => ({
  type: 'tool-call' as const,
  toolCallId: tool.id,
  toolName: 'tool_result',
  args: {
    id: tool.id,
    name: tool.name,
    displayType: 'running',
    ...(tool.caption ? { caption: tool.caption } : {}),
    // The row's time while it runs.
    ...(typeof tool.startedAt === 'number'
      ? { startedAt: tool.startedAt }
      : {}),
  },
});

/** A step the server keeps out of view (a guide it loaded for the turn). */
const HIDDEN_DISPLAY = 'hidden';

/** A stretch Clu spent thinking, once it ended: a "Thought for 2.3s" step. */
const toThoughtPart = (
  messageId: string,
  index: number,
  durationMs: number
) => ({
  type: 'tool-call' as const,
  toolCallId: `${messageId}-thought-${index}`,
  toolName: THOUGHT_PART,
  args: { durationMs },
  // A result, so assistant-ui reads the part as complete.
  result: { durationMs },
});

const toConfirmationPart = (
  request: ConfirmationRequest,
  resolved: boolean = false
) => ({
  type: 'tool-call' as const,
  toolCallId: `confirmation-${request.id}`,
  toolName: 'confirmation_request',
  args: request as unknown as ReadonlyJSONObject,
  // When resolved, provide a result so assistant-ui marks it complete (no spinner)
  ...(resolved
    ? { result: { confirmed: true } as unknown as ReadonlyJSONObject }
    : {}),
});

type AssistantLikeContentPart = Exclude<
  ThreadMessageLike['content'],
  string
>[number];

/**
 * An assistant message's parts for the Gumloop style: text and tool results
 * in the order they arrived, so Clu's narration sits between the steps it
 * came between (each live tool result records how much text had arrived).
 * History carries no such record, so its text goes after the steps (a result
 * with its card is a step too) and before the cards the user acts on, which
 * keeps the text the answer. No reasoning part: the steps line shows the
 * work.
 *
 * Only the pending confirmation is live. A confirmation's id names its action
 * (`confirm-set_agent_status`), so an earlier one for the same action shares
 * it; the single-use action token tells them apart, and the earlier one reads
 * as resolved.
 */
export function gumloopContent(
  message: AgentMessage,
  pendingConfirmationId: string | null,
  pendingActionToken: string | null = null
): AssistantLikeContentPart[] {
  const content: AssistantLikeContentPart[] = [];
  const text = shownText(message);
  const tools = message.toolResults ?? [];
  const shown = (tool: (typeof tools)[number]) =>
    tool.display?.type !== HIDDEN_DISPLAY;
  const running = message.runningTools ?? [];
  // Only a thought that ended is a step: an open one shows as "Thinking.".
  const thoughts = (message.thoughts ?? []).flatMap((spell, index) =>
    typeof spell.durationMs === 'number'
      ? [
          {
            offset: spell.textOffset,
            afterResults: spell.afterResults,
            part: toThoughtPart(
              message.id,
              index,
              spell.durationMs
            ) as AssistantLikeContentPart,
          },
        ]
      : []
  );
  const live =
    (tools.length > 0 || running.length > 0 || thoughts.length > 0) &&
    tools.every((tool) => typeof tool.textOffset === 'number');
  if (live) {
    let cursor = 0;
    // Each thought sits before the results that came after it started.
    const thoughtsBefore = (results: number) =>
      thoughts.filter((thought) => thought.afterResults === results);
    const steps = [
      ...tools.flatMap((tool, index) => [
        ...thoughtsBefore(index),
        ...(shown(tool)
          ? [
              {
                offset: tool.textOffset ?? 0,
                part: toToolResultPart(tool) as AssistantLikeContentPart,
              },
            ]
          : []),
      ]),
      ...thoughts.filter((thought) => thought.afterResults >= tools.length),
      ...running.map((tool) => ({
        offset: tool.textOffset,
        part: toRunningPart(tool) as AssistantLikeContentPart,
      })),
    ];
    for (const step of steps) {
      const end = Math.min(Math.max(step.offset, cursor), text.length);
      if (end > cursor) {
        content.push({ type: 'text', text: text.slice(cursor, end) });
      }
      cursor = end;
      content.push(step.part);
    }
    if (cursor < text.length) {
      content.push({ type: 'text', text: text.slice(cursor) });
    }
  } else {
    const isCard = (tool: (typeof tools)[number]) =>
      INTERACTION_DISPLAYS.has(tool.display?.type ?? '');
    for (const tool of tools) {
      if (!isCard(tool) && shown(tool)) content.push(toToolResultPart(tool));
    }
    if (text) content.push({ type: 'text', text });
    for (const tool of tools) {
      if (isCard(tool)) content.push(toToolResultPart(tool));
    }
  }
  const request = message.confirmationRequest;
  if (request) {
    const isActive =
      request.id === pendingConfirmationId &&
      (!pendingActionToken || request.action_token === pendingActionToken);
    content.push(toConfirmationPart(request, !isActive));
  }
  return content;
}

export const createToThreadMessageLike =
  (
    pendingConfirmationId: string | null,
    gumloop = false,
    pendingActionToken: string | null = null
  ) =>
  (message: AgentMessage): ThreadMessageLike => {
    if (message.role === 'user') {
      return {
        id: message.id,
        role: 'user',
        createdAt: message.timestamp,
        content: [{ type: 'text', text: message.content }],
        ...(message.userInput ||
        message.autoContinue ||
        message.screenshot ||
        message.authorUserId
          ? {
              metadata: {
                custom: {
                  ...(message.userInput
                    ? { userInput: message.userInput }
                    : {}),
                  // A Continue the chat sent itself: the thread shows a marker.
                  ...(message.autoContinue ? { autoContinue: true } : {}),
                  // Sent with a screenshot: the message shows its chip.
                  ...(message.screenshot ? { screenshot: true } : {}),
                  // Who sent it: a teammate's shows their name.
                  ...(message.authorUserId
                    ? { author: message.authorUserId }
                    : {}),
                },
              },
            }
          : {}),
      };
    }

    if (gumloop) {
      return {
        id: message.id,
        role: 'assistant',
        createdAt: message.timestamp,
        content: gumloopContent(
          message,
          pendingConfirmationId,
          pendingActionToken
        ),
        metadata: {
          custom: {
            rawMessageId: message.id,
            startedAt: message.startedAt ?? null,
            completedAt: message.completedAt ?? null,
            acceptedAt: message.acceptedAt ?? null,
            // A reply this tab sent that the server has not taken yet.
            ...(message.acceptedAt === null ? { sending: true } : {}),
            statusLabel: message.statusLabel ?? null,
            model: message.model ?? null,
            ...(message.endedInError ? { endedInError: true } : {}),
          },
        },
      };
    }

    const content: AssistantLikeContentPart[] = [];

    // Part ORDER matters for streaming stability, not just visual layout.
    // assistant-ui binds each rendered part to its array INDEX. If a part is
    // inserted *before* the already-streaming text part mid-stream, the text
    // part's index shifts and assistant-ui briefly hands a non-text part to the
    // `Text` slot — `useMessagePartText` then throws ("MessagePartText can only
    // be used inside text or reasoning message parts"), which used to kill the
    // whole chat. To keep indices append-only we emit parts in arrival order:
    //   reasoning (first event) → text (streams next) → tool results / the
    //   confirmation card (appended as they arrive).
    // Text therefore renders ABOVE its result/confirmation cards, and a tool
    // result arriving after text is a pure append (no index churn, no throw).
    if (message.thinking) {
      content.push({
        type: 'reasoning',
        text: message.thinking,
        parentId: `${message.id}-reasoning`,
      });
    }

    const text = shownText(message);
    if (text) {
      content.push({
        type: 'text',
        text,
      });
    }

    for (const toolResult of message.toolResults ?? []) {
      if (toolResult.display?.type === HIDDEN_DISPLAY) continue;
      content.push(toToolResultPart(toolResult));
    }

    if (message.confirmationRequest) {
      // Mark confirmation as resolved unless it's the currently active pending one
      const isActive = message.confirmationRequest.id === pendingConfirmationId;
      content.push(toConfirmationPart(message.confirmationRequest, !isActive));
    }

    return {
      id: message.id,
      role: 'assistant',
      createdAt: message.timestamp,
      content,
      metadata: {
        custom: {
          rawMessageId: message.id,
          model: message.model ?? null,
        },
      },
    };
  };

/**
 * Whether a confirmation card's time is up (`expires_at` passed), read again
 * the moment it passes. An expired card can't be confirmed or cancelled, so
 * it holds nothing back.
 */
function useCardExpired(expiresAt: string | undefined): boolean {
  const at = expiresAt ? new Date(expiresAt).getTime() : Number.NaN;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Number.isFinite(at)) return;
    const wait = at - Date.now();
    if (wait <= 0) {
      setNow(Date.now());
      return;
    }
    // setTimeout's longest wait; a card never lives that long.
    const id = setTimeout(
      () => setNow(Date.now()),
      Math.min(wait + 50, 2 ** 31 - 1)
    );
    return () => clearTimeout(id);
  }, [at]);
  return Number.isFinite(at) && at <= now;
}

/**
 * True while a question card waits for its answer: Clu asked it and no user
 * message has come after it (see questionCardState).
 */
function questionCardWaits(messages: readonly AgentMessage[]): boolean {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role === 'user') return false;
    const asks = message.toolResults?.some(
      (tool) =>
        tool.display?.type === QUESTION_CARD_DISPLAY &&
        parseQuestionCard(tool.display, tool.result) !== null
    );
    if (asks) return true;
  }
  return false;
}

export const useAgentChatRuntime = (
  context: AgentContext,
  options: UseAgentChatRuntimeOptions = {}
) => {
  const {
    messages,
    isStreaming,
    error,
    errorCode,
    conversationId,
    awaitingConfirmation,
    pendingConfirmation,
    contextMeter,
    progress,
    lastTurn,
    conversations,
    localChats,
    settleAgentChats,
    isLoadingHistory,
    historyFailed,
    historyScope,
    isLoadingConversation,
    sendMessage,
    continuePausedTurn,
    reviewAcceptedPlay,
    retryMessage,
    failedTextTurn,
    hasPendingTextRetry,
    retryFailedTextTurn: retryFailedTextTurnNow,
    confirmAction,
    rejectAction,
    cancelStream,
    clearChat: clearChatNow,
    fetchConversations,
    loadConversation: loadConversationNow,
    deleteConversation: deleteConversationNow,
    snapshotChat,
    replaceChat: replaceChatNow,
    modelPick,
    setModelPick,
  } = useAgentChat(context, {
    sessionKey: options.sessionKey,
    isActive: options.isActive,
    organizationId: options.organizationId,
  });
  const { onNewThread, onOpenConversation } = options;

  // A card waits for the user: a confirmation that hasn't expired. While it
  // does, nothing else sends; an expired one holds nothing back.
  const cardExpired = useCardExpired(pendingConfirmation?.expires_at);
  const cardWaits = awaitingConfirmation && !cardExpired;

  const posthog = usePostHog();
  const { user } = useUser();
  const queuedMessageRef = useRef<string | null>(null);
  const onSelectListRef = useRef(options.onSelectList);
  onSelectListRef.current = options.onSelectList;
  const enabled = options.enabled ?? true;

  // Gumloop's style reads parts in arrival order and keeps consecutive
  // assistant messages apart (a greeting after an earlier reply has its own
  // header); every other context converts exactly as before.
  const gumloop = useGumloopChat();
  // The message queue belongs to Gumloop's composer, so it follows it: off
  // it, a message held here goes out when the turn ends, as before.
  const queueEnabled = gumloop;
  // Whether the composer holds a draft, for the turn-end effect below.
  const composerDraftRef = useRef(false);
  const pendingSend = useAppSelector(selectAiChatPendingSend);
  const initialMessage = useAppSelector(selectAiChatInitialMessage);
  const questionWaits = queueEnabled && questionCardWaits(messages);

  // One queue per chat, mirrored in a ref for synchronous reads.
  const [queues, setQueues] = useState<MessageQueues>(NO_QUEUES);
  const queuesRef = useRef(queues);
  const commitQueues = useCallback((next: MessageQueues) => {
    if (next === queuesRef.current) return;
    queuesRef.current = next;
    setQueues(next);
  }, []);
  const queuedIdRef = useRef(0);
  const chatKey = queueKey(conversationId);
  // The chat as this render sees it, for handlers the runtime calls later.
  const liveRef = useRef({
    queueEnabled,
    chatKey,
    isStreaming,
    awaitingConfirmation: cardWaits,
    hasPendingTextRetry,
    messages,
  });
  liveRef.current = {
    queueEnabled,
    chatKey,
    isStreaming,
    awaitingConfirmation: cardWaits,
    hasPendingTextRetry,
    messages,
  };
  // A new chat's queue, left behind by a switch, for the composer of the chat
  // shown next; and whether a switch happened since the composer settled.
  const composerReturnRef = useRef<string | null>(null);
  const leftChatRef = useRef(false);
  const [leaveSeq, setLeaveSeq] = useState(0);

  // Off Gumloop's queue: a message sent while a turn runs waits here and goes
  // out when it ends.
  useEffect(() => {
    if (isStreaming || !queuedMessageRef.current) return;
    const queuedMessage = queuedMessageRef.current;
    queuedMessageRef.current = null;
    void sendMessage(queuedMessage);
  }, [isStreaming, sendMessage]);

  /**
   * Leaving the chat on screen (another chat, New chat, an agent's workspace
   * in or out). Its turn is aborted, so a chat with an id keeps its queue,
   * held. So does a new chat Clu has answered in, under its latest turn's
   * key, until the chat opens again (startedChatKey). A new chat left before
   * Clu answered may not exist, so its text goes back into the composer and
   * nothing is sent.
   */
  const leaveChat = useCallback(() => {
    const live = liveRef.current;
    if (!live.queueEnabled) return;
    let kept = queuesRef.current;
    if (live.chatKey !== NEW_CHAT) {
      kept = holdQueue(kept, live.chatKey);
    } else {
      const startedKey = startedChatKey(live.messages);
      if (startedKey)
        kept = holdQueue(moveQueue(kept, NEW_CHAT, startedKey), startedKey);
    }
    const [next, text] = takeAll(kept, NEW_CHAT);
    commitQueues(next);
    if (text) {
      composerReturnRef.current = joinMessages([
        composerReturnRef.current,
        text,
      ]);
    }
    leftChatRef.current = true;
    setLeaveSeq((seq) => seq + 1);
  }, [commitQueues]);

  const clearChat = useCallback(() => {
    leaveChat();
    clearChatNow();
  }, [clearChatNow, leaveChat]);

  const loadConversation = useCallback(
    (id: string) => {
      leaveChat();
      return loadConversationNow(id);
    },
    [leaveChat, loadConversationNow]
  );

  const replaceChat = useCallback(
    (snapshot: ChatSnapshot) => {
      leaveChat();
      replaceChatNow(snapshot);
    },
    [leaveChat, replaceChatNow]
  );

  /** A deleted chat's queue goes with it. */
  const deleteConversation = useCallback(
    async (id: string): Promise<boolean> => {
      const deleted = await deleteConversationNow(id);
      if (deleted) commitQueues(takeAll(queuesRef.current, id)[0]);
      return deleted;
    },
    [commitQueues, deleteConversationNow]
  );

  // A new chat's queue follows the chat once its id is known. A switch that
  // lands on a chat (after emptying the new chat's queue) holds what was
  // queued while it loaded.
  useLayoutEffect(() => {
    if (!queueEnabled || !conversationId) return;
    let next = moveQueue(queuesRef.current, NEW_CHAT, conversationId);
    if (leftChatRef.current) next = holdQueue(next, conversationId);
    commitQueues(next);
  }, [commitQueues, conversationId, queueEnabled]);

  // A new chat's queue left under its turn's key comes back, held, when the
  // chat shows again: opened by its id, or put back after an agent's
  // workspace. Not while another chat opens, since the old one still shows.
  useLayoutEffect(() => {
    if (!queueEnabled || isLoadingConversation) return;
    const keys = queueKeysOfChat(queuesRef.current, messages);
    if (keys.length === 0) return;
    let next = queuesRef.current;
    for (const key of keys) next = moveQueue(next, key, chatKey);
    commitQueues(holdQueue(next, chatKey));
  }, [chatKey, commitQueues, isLoadingConversation, messages, queueEnabled]);

  // When a turn ends. A layout effect, so no frame shows the queue idle
  // before it goes out.
  const handledTurnRef = useRef(lastTurn?.seq ?? 0);
  useLayoutEffect(() => {
    if (!lastTurn || lastTurn.seq === handledTurnRef.current) return;
    handledTurnRef.current = lastTurn.seq;
    // A later turn already runs: this one's end changes nothing.
    if (isStreaming) return;
    const key = queueKey(lastTurn.conversationId);
    if (queueEnabled) {
      if (lastTurn.outcome !== 'done') {
        // Stopped or failed: the queue waits for the user. Waiting on a
        // confirmation card: it waits for the card's answer, and goes out at
        // the end of the turn Cancel or Confirm starts.
        commitQueues(
          lastTurn.outcome === 'awaiting_confirmation'
            ? holdForCard(queuesRef.current, key)
            : holdQueue(queuesRef.current, key)
        );
        return;
      }
      const queue = queuesRef.current[key];
      if (queue && !queue.held && key === chatKey) {
        // Ended on a question card: the queue waits for its answer, as for a
        // confirmation card. Sent now, it would pass the card over unseen.
        if (questionWaits) {
          commitQueues(holdForCard(queuesRef.current, key));
          return;
        }
        // A card's or the launcher's send goes first; the two would abort each
        // other. The queue goes at the end of that turn.
        if (pendingSend || initialMessage) return;
        const [next, text] = takeAll(queuesRef.current, key);
        if (text) {
          commitQueues(next);
          void sendMessage(text);
          return;
        }
      }
    }
    // A turn that stopped at a limit of its own (not a failure) goes on by
    // itself, after what the user queued: Continue, up to three times for one
    // message, then the Continue button the turn ended with. Only when the
    // server says a fresh turn would have the same room (`autoContinue`).
    // Anything else sending, a question card waiting, a draft in the composer
    // (a correction the user is typing), messages the user queued and holds
    // (Stop, a failure: they wait for Send now), or a chat that moved on, goes
    // first and leaves the Continue button.
    if (
      lastTurn.outcome === 'done' &&
      lastTurn.pause?.autoContinue &&
      key === chatKey &&
      !pendingSend &&
      !initialMessage &&
      !questionCardWaits(liveRef.current.messages) &&
      !composerDraftRef.current &&
      !queuesRef.current[key]?.items.length &&
      // Off the queue (no Gumloop composer), a message held for the turn's
      // end goes first too.
      !queuedMessageRef.current
    )
      void continuePausedTurn(lastTurn);
  }, [
    chatKey,
    commitQueues,
    continuePausedTurn,
    initialMessage,
    isStreaming,
    lastTurn,
    pendingSend,
    queueEnabled,
    questionWaits,
    sendMessage,
  ]);

  // The question card answered (a user message follows it now) releases
  // what it held: the queue goes out at the end of the answer's turn.
  const questionWaitedRef = useRef(questionWaits);
  useLayoutEffect(() => {
    const waited = questionWaitedRef.current;
    questionWaitedRef.current = questionWaits;
    if (!waited || questionWaits || cardWaits) return;
    commitQueues(releaseCardHold(queuesRef.current, chatKey));
  }, [cardWaits, chatKey, commitQueues, questionWaits]);

  const sendNow = useCallback(() => {
    const live = liveRef.current;
    if (live.isStreaming || live.awaitingConfirmation) return;
    const [next, text] = takeAll(queuesRef.current, live.chatKey);
    if (!text) return;
    commitQueues(next);
    void sendMessage(text);
  }, [commitQueues, sendMessage]);

  const removeQueued = useCallback(
    (id: string) => {
      commitQueues(
        removeQueuedMessage(queuesRef.current, liveRef.current.chatKey, id)
      );
    },
    [commitQueues]
  );

  const editQueued = useCallback(
    (id: string): string | null => {
      const [next, item] = takeQueued(
        queuesRef.current,
        liveRef.current.chatKey,
        id
      );
      commitQueues(next);
      return item?.text ?? null;
    },
    [commitQueues]
  );

  const handleNew = useCallback<ExternalStoreAdapter<AgentMessage>['onNew']>(
    async (message) => {
      const text = extractMessageText(message).trim();
      // A screenshot goes with this message only; it never joins the queue.
      const image = firstChatImage(message);
      if (!text && !image) return;

      const live = liveRef.current;
      if (image) {
        // The composer holds a staged screenshot while a turn runs or a card
        // waits, so one reaching here then has nowhere to go.
        if (live.isStreaming || live.awaitingConfirmation) return;
        const [next, queued] = live.queueEnabled
          ? takeAll(queuesRef.current, live.chatKey)
          : [queuesRef.current, null];
        commitQueues(next);
        await sendMessage(joinMessages([queued, text]), { image });
        return;
      }

      if (live.queueEnabled) {
        // While Clu answers it waits for the turn to end. While a
        // confirmation card waits nothing goes out, as Send and Send now are
        // off: it waits with anything already queued until the card is
        // answered, then goes at the end of the answer's turn. Typing never
        // answers a card: only its own Cancel or Confirm does.
        if (live.isStreaming || live.awaitingConfirmation) {
          queuedIdRef.current += 1;
          const next = enqueue(queuesRef.current, live.chatKey, {
            id: `queued-${queuedIdRef.current}`,
            text,
          });
          commitQueues(
            live.isStreaming ? next : holdForCard(next, live.chatKey)
          );
          return;
        }
        // Idle with messages waiting: they go out with this one, as one.
        const [next, queued] = takeAll(queuesRef.current, live.chatKey);
        commitQueues(next);
        await sendMessage(joinMessages([queued, text]));
        return;
      }

      if (isStreaming) {
        // A second message sent before the turn ends joins the first, so
        // neither is dropped.
        const queued = queuedMessageRef.current;
        queuedMessageRef.current = queued ? `${queued}\n\n${text}` : text;
        return;
      }

      await sendMessage(text);
    },
    [commitQueues, isStreaming, sendMessage]
  );

  /**
   * Answering the card (Cancel or Confirm) releases what it held: the queue
   * goes out at the end of the answer's turn, unless that turn fails, stops
   * or ends on another card. A hold for any other reason stays.
   */
  const releaseForAnswer = useCallback(() => {
    const live = liveRef.current;
    if (!live.queueEnabled || !live.awaitingConfirmation) return;
    commitQueues(releaseCardHold(queuesRef.current, live.chatKey));
  }, [commitQueues]);

  const confirmCard = useCallback<typeof confirmAction>(
    (selection, expected) => {
      releaseForAnswer();
      return confirmAction(selection, expected);
    },
    [confirmAction, releaseForAnswer]
  );

  /**
   * Cancel: what the user typed while the card waited is why, so it goes to
   * Clu with the cancel instead of after it. A cancel that does not go
   * through puts those words back.
   */
  const rejectCard = useCallback<typeof rejectAction>(async () => {
    const live = liveRef.current;
    let said: readonly QueuedMessage[] = [];
    if (live.queueEnabled && live.awaitingConfirmation) {
      const [next, items] = takeCardHold(queuesRef.current, live.chatKey);
      commitQueues(next);
      said = items;
    }
    releaseForAnswer();
    const result = await rejectAction(
      joinMessages(said.map((item) => item.text)) || undefined
    );
    if (said.length > 0 && !result?.ok && !result?.aborted) {
      commitQueues(
        returnCardHold(
          queuesRef.current,
          live.chatKey,
          said,
          result?.errorCode !== 'approval_restart_required'
        )
      );
    }
    return result;
  }, [commitQueues, rejectAction, releaseForAnswer]);

  const retryFailedTextTurn = useCallback(
    (id: string) =>
      retryFailedTextTurnNow(id, () => {
        // A first turn can name its chat and fail in one reader batch, before
        // the new-chat queue migrates. Hold both keys without consuming either.
        const live = liveRef.current;
        if (live.queueEnabled) {
          commitQueues(
            holdQueue(holdQueue(queuesRef.current, NEW_CHAT), live.chatKey)
          );
        }
      }),
    [commitQueues, retryFailedTextTurnNow]
  );

  const handleReload = useCallback(
    async (parentId: string | null) => {
      if (isStreaming) return;
      // Regenerate resends text only, so a screenshot turn would go out as
      // the word "Screenshot": the thread offers no Regenerate there.
      const resendsScreenshot = (m: AgentMessage) =>
        m.screenshot === true || m.content === SCREENSHOT_MESSAGE;

      // Find the user message that is the parent of the assistant message being regenerated.
      // assistant-ui passes the parentId which is the message ID of the user message.
      if (parentId) {
        const userMsg = messages.find((m) => m.id === parentId);
        if (userMsg?.content) {
          if (resendsScreenshot(userMsg)) return;
          await retryMessage(userMsg);
          return;
        }
      }

      // Fallback: resend the last user message in the conversation
      const lastUserMsg = [...messages]
        .reverse()
        .find((m) => m.role === 'user');
      if (lastUserMsg?.content && !resendsScreenshot(lastUserMsg)) {
        await retryMessage(lastUserMsg);
      }
    },
    [isStreaming, messages, retryMessage]
  );

  const pendingActionToken = gumloop
    ? (pendingConfirmation?.action_token ?? null)
    : null;
  const toThreadMessageLike = useMemo(
    () =>
      createToThreadMessageLike(
        pendingConfirmation?.id ?? null,
        gumloop,
        pendingActionToken
      ),
    [pendingConfirmation?.id, gumloop, pendingActionToken]
  );

  const continuedMessages = useMemo(
    () => markContinuedOn(messages),
    [messages]
  );
  const convertedMessages = useExternalMessageConverter({
    callback: toThreadMessageLike,
    messages: continuedMessages,
    isRunning: isStreaming,
    joinStrategy: gumloop ? 'none' : undefined,
  });

  const threadListAdapter = useMemo<
    NonNullable<ExternalStoreAdapter<AgentMessage>['adapters']>['threadList']
  >(
    () => ({
      threadId: conversationId ?? undefined,
      isLoading: isLoadingHistory,
      threads: conversations.map((conversation) => ({
        id: conversation.id,
        title: conversation.title ?? undefined,
        status: 'regular' as const,
      })),
      onSwitchToNewThread: onNewThread ?? clearChat,
      onSwitchToThread: onOpenConversation ?? loadConversation,
      onDelete: async (id: string) => {
        await deleteConversation(id);
      },
    }),
    [
      clearChat,
      conversationId,
      conversations,
      deleteConversation,
      loadConversation,
      isLoadingHistory,
      onNewThread,
      onOpenConversation,
    ]
  );

  const cancelAcceptedReviews = useRef<() => void>(() => {});
  const adapter = useMemo<ExternalStoreAdapter>(
    () => ({
      isRunning: isStreaming,
      isLoading: isLoadingConversation,
      messages: convertedMessages,
      suggestions: SUGGESTED_PROMPTS,
      onNew: handleNew,
      onEdit: handleNew,
      onReload: handleReload,
      onCancel: async () => {
        cancelAcceptedReviews.current();
        // Held at once: Stop drops isStreaming a tick before the abort
        // reports how the turn ended.
        const live = liveRef.current;
        if (live.queueEnabled) {
          commitQueues(holdQueue(queuesRef.current, live.chatKey));
        }
        cancelStream();
      },
      adapters: {
        threadList: threadListAdapter,
        attachments: SCREENSHOT_ATTACHMENTS,
        feedback: {
          submit: async ({ type, message }) => {
            posthog?.capture('copilot_message_feedback', {
              type,
              message_id: message.id,
              conversation_id: conversationId,
              trace_id: `feedback-${conversationId}-${message.id}-${Date.now()}`,
              user_id: user?.id,
              // The active org (PostHog's registered group), not the
              // user's first membership.
              org_id: posthog?.getGroups?.()?.organization,
              context_type: context.type,
              lead_list_id: context.lead_list_id,
            });
          },
        },
      },
    }),
    [
      cancelStream,
      commitQueues,
      context.lead_list_id,
      context.type,
      conversationId,
      convertedMessages,
      handleNew,
      handleReload,
      isLoadingConversation,
      isStreaming,
      posthog,
      threadListAdapter,
      user,
    ]
  );

  const runtime = useExternalStoreRuntime(adapter);
  const [hasComposerDraft, setHasComposerDraft] = useState(false);
  useEffect(() => {
    const composer = runtime.thread.composer;
    const update = () => {
      const draft = Boolean(composer.getState().text.trim());
      composerDraftRef.current = draft;
      setHasComposerDraft(draft);
    };
    update();
    return composer.subscribe(update);
  }, [runtime]);
  const {
    reviews: acceptedReviewRows,
    retry: retryAcceptedReview,
    cancel: cancelAcceptedReview,
  } = useAcceptedPlayReview({
    requests: options.acceptedPlayReviews ?? NO_ACCEPTED_REVIEWS,
    ready:
      enabled &&
      context.type === 'agent' &&
      !!conversationId &&
      messages.length > 0 &&
      !isStreaming &&
      !isLoadingConversation &&
      !cardWaits &&
      !questionWaits &&
      !hasPendingTextRetry &&
      !hasComposerDraft &&
      !queues[chatKey]?.items.length &&
      !queuedMessageRef.current &&
      !(options.isActive && (pendingSend || initialMessage)),
    historyFailed: !isLoadingConversation && messages.length === 0 && !!error,
    send: reviewAcceptedPlay,
  });
  const acceptedReviewRetry = useRef<{
    token: object;
    proposalId: string;
  } | null>(null);
  const retryAcceptedPlayReview = useCallback(
    async (proposalId: string) => {
      const review = acceptedReviewRows.find(
        (item) => playReviewKey(item) === proposalId
      );
      if (!review?.retryable) return;
      // An offered check waits in the queue for the chat to be free.
      if (
        review.status !== 'offered' &&
        (liveRef.current.isStreaming || liveRef.current.hasPendingTextRetry)
      )
        return;
      if (
        review.errorCode === 'accepted_review_history' ||
        review.errorCode === 'accepted_review_waiting'
      ) {
        const token = {};
        acceptedReviewRetry.current = { token, proposalId };
        await loadConversation(review.conversationId);
        // Stop or a newer retry invalidates this read's continuation.
        if (acceptedReviewRetry.current?.token !== token) return;
        acceptedReviewRetry.current = null;
      }
      retryAcceptedReview(proposalId);
    },
    [acceptedReviewRows, retryAcceptedReview, loadConversation]
  );
  cancelAcceptedReviews.current = () => {
    const retrying = acceptedReviewRetry.current;
    acceptedReviewRetry.current = null;
    cancelAcceptedReview(retrying?.proposalId);
  };

  // The chat being named mid turn swaps the runtime's thread, and with it the
  // composer, which comes back empty: the text in it just before is kept to
  // put back. These effects run after the runtime's own, so the swap is done.
  const draftBeforeSwapRef = useRef<string | null>(null);
  useEffect(() => {
    if (!queueEnabled) return;
    const composer = runtime.thread.composer;
    let threadId = runtime.threads.getState().mainThreadId;
    let text = composer.getState().text;
    return composer.subscribe(() => {
      const nextThreadId = runtime.threads.getState().mainThreadId;
      if (nextThreadId !== threadId) {
        threadId = nextThreadId;
        draftBeforeSwapRef.current = text;
      }
      text = composer.getState().text;
    });
  }, [queueEnabled, runtime]);

  // The composer once the chat on screen settles: a new chat's queue left
  // behind goes back in ahead of the draft, and a chat named mid turn gets
  // its draft back. Any other switch leaves the draft as it always has.
  const settledIdRef = useRef(conversationId);
  useEffect(() => {
    if (!queueEnabled || isLoadingConversation) return;
    const previousId = settledIdRef.current;
    settledIdRef.current = conversationId;
    const left = leftChatRef.current;
    leftChatRef.current = false;
    const named = !left && previousId === null && conversationId !== null;
    const returned = composerReturnRef.current;
    composerReturnRef.current = null;
    const carried = named || returned ? draftBeforeSwapRef.current : null;
    draftBeforeSwapRef.current = null;
    if (!returned && !carried) return;
    const composer = runtime.thread.composer;
    const draft = composer.getState().text;
    const kept = draft || carried || '';
    const text = returned ? joinMessages([returned, kept]) : kept;
    if (text !== draft) composer.setText(text);
  }, [conversationId, isLoadingConversation, leaveSeq, queueEnabled, runtime]);

  const queuedItems = queues[chatKey]?.items ?? NO_QUEUED;
  const messageQueue = useMemo<MessageQueue | null>(
    () =>
      queueEnabled
        ? {
            items: queuedItems,
            canSendNow: queuedItems.length > 0 && !isStreaming,
            sendDisabled: cardWaits,
            sendNow,
            remove: removeQueued,
            edit: editQueued,
          }
        : null,
    [
      cardWaits,
      editQueued,
      isStreaming,
      queueEnabled,
      queuedItems,
      removeQueued,
      sendNow,
    ]
  );

  // --- List mention adapter (for @ trigger) ---
  const listMentionItems = useMemo<Unstable_MentionItem[]>(
    () => [
      {
        id: 'all',
        type: 'list',
        label: 'All connected data',
        description: 'Search across everything',
      },
      ...(options.lists ?? []).map((list) => ({
        id: list.id,
        type: 'list',
        label: list.name,
        description: `${list.prospect_count ?? 0} leads`,
        metadata: {
          listId: list.id,
          listName: list.name,
          leadCount: list.prospect_count ?? 0,
        } as unknown as ReadonlyJSONObject,
      })),
    ],
    [options.lists]
  );

  // Expose a single "lists" category so the library's navigableList is
  // non-empty the instant `@` is typed (empty query). The popover's
  // auto-select effect in <Thread /> enters this category on open, so
  // arrow keys navigate items directly without a manual Enter-to-enter
  // step. `search` still powers the post-character filter path.
  const listMentionAdapter = useMemo<Unstable_MentionAdapter>(
    () => ({
      categories: () => [{ id: LIST_MENTION_CATEGORY_ID, label: 'Lists' }],
      categoryItems: (categoryId) =>
        categoryId === LIST_MENTION_CATEGORY_ID ? listMentionItems : [],
      search: (query) => {
        if (!query) return listMentionItems;
        const lq = query.toLowerCase();
        return listMentionItems.filter((item) =>
          item.label.toLowerCase().includes(lq)
        );
      },
    }),
    [listMentionItems]
  );

  const listMentionFormatter = useMemo<Unstable_DirectiveFormatter>(
    () => ({
      serialize: (item) => {
        // NOTE: This side effect (dispatching to Redux) is intentional and unavoidable.
        // assistant-ui's Unstable_DirectiveFormatter does not expose a separate onSelect
        // lifecycle hook for mentions, so serialize is the only place where we can react
        // to a mention being chosen by the user. Moving this logic elsewhere (e.g. a
        // useEffect watching composer text) would introduce race conditions or require
        // parsing @-mention syntax out of raw text. This is a known limitation of the
        // current assistant-ui mention system.
        if (item.id === 'all') {
          onSelectListRef.current?.(null);
        } else {
          const list = options.lists?.find((l) => l.id === item.id);
          if (list) onSelectListRef.current?.(list);
        }
        return ''; // Don't insert text, just update the context chip
      },
      parse: (text) => [{ kind: 'text', text }],
    }),
    [options.lists]
  );

  return {
    runtime,
    acceptedPlayReviews: acceptedReviewRows,
    retryAcceptedPlayReview,
    messages,
    isStreaming,
    error,
    errorCode,
    conversationId,
    // An expired card waits for nothing: Send is on again.
    awaitingConfirmation: cardWaits,
    pendingConfirmation,
    contextMeter,
    progress,
    modelPick,
    setModelPick,
    conversations,
    localChats,
    settleAgentChats,
    isLoadingHistory,
    historyFailed,
    historyScope,
    isLoadingConversation,
    sendMessage,
    failedTextTurn: cardWaits || questionWaits ? null : failedTextTurn,
    retryFailedTextTurn,
    confirmAction: confirmCard,
    rejectAction: rejectCard,
    cancelStream,
    clearChat,
    fetchConversations,
    loadConversation,
    deleteConversation,
    snapshotChat,
    replaceChat,
    messageQueue,
    listMentionAdapter,
    listMentionFormatter,
    listMentionItems,
  };
};
